// In order to encourage separation between UI-specific types vs. types defined
// on the backend, this file *should only contain* types that relate to data defined
// on the server.
// Ideally, this file will become obsolete once we are to capture server types
// via protobuf --> TypeScript generator.

import { FallbackColor } from '@cbhq/cds-common';

import {
  Account,
  AccountCredit,
  AccountItem,
  BillingHistoryItem,
  CoinbaseSmartContract,
  CoinbaseWebhook,
  GasPolicy,
  ICluster,
  IClusterLimits,
  IPlatformAPIKey,
  IPlatformToken,
  ITradePortfolio,
  LegacyUser,
  OrganizationRole,
  Product,
  ProductDetails,
  ProductTypeCOMS,
  Project,
  ProtocolKey,
  User,
} from './ts_types';

export type GetAccountResponse = Account;

export type CancelAccountItemResponse = {
  canceledAt: string;
};

export type GetAccountItemsResponse = {
  accountItems: AccountItem[];
  nextPageToken: string;
};

export type GetBillingHistoryItemsResponse = {
  accountInvoices: BillingHistoryItem[];
};

export type DownloadInvoiceResponse = {
  downloadUri: string;
};

export type CreateAccountItemRequest = {
  productVariantId: string;
  projectId: string;
};

export type CreateAccountItemResponse = AccountItem;

export type ChangeAccountItemRequest = {
  productVariantId: string;
  itemId: string;
};

export type ChangeAccountItemResponse = AccountItem;

export type GetAccountCreditsResponse = {
  accountCredits: AccountCredit[];
  nextPageToken: string;
};

export type CreateCommerceChargeResponse = {
  hostedUrl: string;
};

export type GetProductsResponse = { products: Product[] };

export type CreateProjectRequest = {
  name?: string;
  organizationId: string;
};

export type DeleteProjectRequest = {
  organizationId: string;
  projectId: string;
};

export type UpdateProjectRequest = Pick<Project, 'id' | 'name' | 'organizationId'>;

export type CreateOrUpdateProjectResponse = { project: Project };

export type CreatePlatformAPIKeyResponse = IPlatformAPIKey;

export type CreatePlatformTokenResponse = IPlatformToken;

export type CreateWebhookResponse = CoinbaseWebhook;

export type DeleteWebhookRequest = {
  orgId: string;
  projectId: string;
  webhookId: string;
};

export type DeleteWebhookResponse = {
  success: boolean;
};

export type GetWebhooksResponse = {
  data: (CoinbaseWebhook & { id: string })[];
  nextPageToken: string;
};

export type CreateSmartContractResponse = CoinbaseSmartContract;

export type UpdateSmartContractResponse = CoinbaseSmartContract;

export type DeleteSmartContractRequest = {
  orgId: string;
  projectId: string;
  smartContractId: string;
};

export type DeleteSmartContractResponse = {
  success: boolean;
};

export type GetSmartContractsResponse = {
  data: CoinbaseSmartContract[];
  nextPageToken: string;
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DeletePlatformAPIKeyResponse {}

export interface DeletePlatformAPIKeyRequest {
  name: string;
}

export interface GetTradePortfoliosResponse {
  portfolios: ITradePortfolio[];
}

export interface GetPlatformAPIKeyResponse {
  apiKeys: IPlatformAPIKey[];
  nextPageToken: string;
}

export interface GetPlatformTokenResponse {
  tokens: IPlatformToken[];
  nextPageToken: string;
}

export type RotatePlatformTokenResponse = IPlatformToken;

export type ConfigurePlatformTokenResponse = IPlatformToken;

type PaymentType = 'card' | 'sepa_debit' | 'us_bank_account';

export interface GetPaymentSetupSecretResponse {
  type: PaymentType;
  paymentSecret: string;
}

export type PaymentCard = {
  cardholder: string;
  network: 'visa' | 'mastercard' | 'amex' | string;
  lastFour: string;
  expirationMonth: number;
  expirationYear: number;
};

export type PaymentUsBankAccount = {
  bank: string;
  lastFour: string;
};

export type PaymentSepaDebit = {
  bankCode: string;
  branchCode: string;
  country: string;
  lastFour: string;
};

interface PaymentMethodDetails {
  type: PaymentType;
  /**
   * @deprecated This is a legacy field to maintain backwards compatibility. Use the `creditCard` field on `PaymentMethodCard` instead.
   */
  card?: PaymentCard;
  externalId: string;
}

interface PaymentMethodCard {
  type: 'card';
  creditCard: PaymentCard;
}
interface PaymentMethodUsBankAccount {
  type: 'us_bank_account';
  usBankAccount: PaymentUsBankAccount;
}

interface PaymentMethodSepaDebit {
  type: 'sepa_debit';
  sepaDebit: PaymentSepaDebit;
}

export type PaymentMethod = PaymentMethodDetails &
  (PaymentMethodCard | PaymentMethodSepaDebit | PaymentMethodUsBankAccount);

export type GetPaymentMethodResponse = PaymentMethod;

export type DeletePaymentMethodResponse = {
  type?: PaymentType;
  card?: PaymentCard;
  id: string;
};

/** Organization IAM Request Response Codes */

export type InviteMemberResponse = {
  success: boolean;
};
export type RemoveMemberResponse = {
  success: boolean;
};
export type SwitchMemberResponse = {
  success: boolean;
};

/** *****************************************************************************
 * ATTENTION: These are the new response types for organizations.
 * These types reflect the updated structure from the Organization Management Service.
 * They should be used for all new development and gradually replace legacy types.
 ****************************************************************************** */

export interface OrganizationUserDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: OrganizationRole;
  avatarUrl?: string;
  createdAt?: string;
  displayName: string;
}

export interface OrganizationInviteDetails {
  email: string;
  role: OrganizationRole;
  avatarUrl?: string;
}

export interface OrganizationDetails {
  id: string;
  name: string;
  users: OrganizationUserDetails[];
  invites: OrganizationInviteDetails[];
}

interface UserOrganization {
  userId: string;
  organization: OrganizationDetails;
  role: OrganizationRole;
}

export interface GetUserOrganizationsResponse {
  organizations: UserOrganization[];
  legacyResponse: any;
}

type UserStatus = {
  status: 'active' | 'invited';
};

export type ActiveUserType = OrganizationUserDetails &
  UserStatus & { fullNameOrEmail: string; avatarColor: FallbackColor };
export type InvitedUserType = OrganizationInviteDetails & UserStatus;

export type OrganizationUser = ActiveUserType | InvitedUserType;

/** *****************************************************************************
 * END: New organization response types
 ****************************************************************************** */

/** *****************************************************************************
 * ATTENTION: These are the new response types for projects.
 ****************************************************************************** */

export interface ProjectsForUserNEW {
  projects: ProjectNEW[];
}

interface ProjectNEW {
  id: string;
  name: string;
  products: ProductTypeNEW[];
  organizationId: string;
  active: boolean;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  isTestnet: boolean;
  users: ProjectUserDetailsNEW[];
  invites: ProjectInviteNEW[];
}

export type ProjectWithRole = ProjectNEW & { userRole: ProjectRoleNEW };

export interface ProjectUserDetailsNEW {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: ProjectRoleNEW;
  avatarUrl?: string;
  createdAt?: string;
  displayName: string;
}

export interface ProjectInviteNEW {
  id: string;
  project: ProjectNEW;
  email: string;
  role: ProjectRoleNEW;
  accepted: boolean;
  invitedByUser: any; // Simplified for now
  inviteToken: string;
  hasRetrieved: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ProjectTeamMember = ProjectUserDetailsNEW | ProjectInviteNEW;

enum ProductTypeNEW {
  Unspecified = 'PRODUCT_TYPE_UNSPECIFIED',
  Waas = 'PRODUCT_TYPE_WAAS',
  StakingApi = 'PRODUCT_TYPE_STAKING_API',
  Rat = 'PRODUCT_TYPE_RAT',
  Base = 'PRODUCT_TYPE_BASE',
  CbPay = 'PRODUCT_TYPE_CB_PAY',
  WaasConsumer = 'PRODUCT_TYPE_WAAS_CONSUMER',
}

export enum ProjectRoleNEW {
  Unspecified = 'UNSPECIFIED',
  Owner = 'OWNER',
  Admin = 'ADMIN',
  UserNEW = 'USER',
}

export interface ProjectInvite {
  id: string;
  project: ProjectNEW;
  email: string;
  role: ProjectRoleNEW;
  accepted: boolean;
  invitedByUser: ProjectUserDetailsNEW;
  inviteToken: string;
  hasRetrieved: boolean;
  createdAt: string;
  updatedAt: string;
}

/** *****************************************************************************
 * END: New project response types
 ****************************************************************************** */

export interface OrganizationInvite {
  id: string;
  organization: {
    id: string;
    name: string;
  };
  email: string;
  inviteToken: string;
  role: string;
  accepted: boolean;
  hasRetrieved: boolean;
  invitedByUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  createdAt: Date;
  updatedAt: Date;
}
/** Resource Request Response Codes */
export type GetResourceCustomerLimitsResponse = IClusterLimits[];

export type DeleteResourceRequest = {
  name: string;
  type: string;
};

export type GetResourceResponse = {
  resources: ICluster[];
};

export type CreateResourceResponse = {
  id: string;
  name: string;
  type: ProtocolKey;
};

/** Self */ export type GetUserResponse = User & {
  legacyResponse: LegacyUser;
};

export type GasPolicyResponse = {
  policy: GasPolicy;
};

export type CreateOrUpdateProductDetailsRequest = {
  project: Project;
  productType: ProductTypeCOMS;
  productDetails: ProductDetails;
  payload?: Record<string, any>;
};

export type CreateOrUpdateProductDetailsResponse = ProductDetails;

type CreateClientFormData = Pick<
  ClientRecordNonSensitive,
  | 'name'
  | 'owner'
  | 'redirectUris'
  | 'appType'
  | 'servicesOffered'
  | 'description'
  | 'notificationsUrl'
  | 'developerWebsite'
  | 'icon'
>;
export interface CreateOAuthClientRequest {
  parent: string;
  clientRecordNonSensitive: CreateClientFormData;
}

export interface CreateOAuthClientResponse {
  clientInfo: ClientRecordNonSensitive;
  clientRecordSensitive: ClientRecordSensitive;
  fragment: ClientFragment;
}

export interface GetOAuthClientResponse {
  oauthClients: {
    clientRecordNonSensitive: ClientRecordNonSensitive;
    clientRecordSensitive: ClientRecordSensitive;
    clientFragment: ClientFragment;
  }[];
}

export interface UpdateOAuthClientRequest {
  clientRecord: { name: string; clientRecordNonSensitive: ClientRecordNonSensitive };
}

export interface UpdateOAuthClientResponse {
  clientRecordNonSensitive: ClientRecordNonSensitive;
  clientFragment: ClientFragment;
  clientRecordSensitive: ClientRecordSensitive;
  name: string;
}

export interface DeleteOAuthClientResponse {
  deleteType: ClientDeleteType;
  clientId: string;
}

// Supporting Types
export interface ClientRecordNonSensitive {
  disabled: boolean;
  clientId: string;
  name: string;
  owner: string;
  redirectUris: string;
  grantTypes: string;
  responseTypes: string;
  scope: string;
  audience: string;
  termsOfServiceUri: string;
  clientUri: string;
  logoUri: string;
  tokenEndpointAuthMethod: string;
  createdAt: string;
  updatedAt: string;
  metadata: any;
  appType: ApplicationType;
  servicesOffered: ServicesOffered;
  description: string;
  notificationsUrl: string;
  developerName: string;
  developerWebsite: string;
  icon: string; // Base64 encoded string
}

export interface ClientRecordSensitive {
  clientId: string;
  clientSecret: string;
  secretExpiresAt: string;
}

enum ClientFragment {
  CLIENT_RECORD_INFO = 0,
  CLIENT_RECORD_WITH_SENSITIVE_INFO = 1,
  CLIENT_PROFILE_INFO = 2,
}

export enum ClientDeleteType {
  SOFT_DELETE = 0,
  HARD_DELETE = 1,
}

export enum ApplicationType {
  OTHER = '0',
  ECOMMERCE = '1',
  MICROPAYMENTS = '2',
  BITCOIN_WALLET = '3',
  GAMING = '4',
  FINANCIAL_SERVICES = '5',
}

export interface ServicesOffered {
  remittances: boolean;
  financialServices: boolean;
  gambling: boolean;
  currencyExchange: boolean;
}

export type GenerateProductReportingResponse = {
  runId: string;
};

export type GetProductReportingStatusResponse = {
  status: string;
  s3DownloadUrl: string;
};

export type BuildQuestItem = {
  questionId: string;
  complete: boolean;
  pointValue: number;
  pointsAwarded: number;
  updatedAt: string | null;
  userAnswer: string;
};

export type GetBuildQuestStatusResponse = {
  items: BuildQuestItem[];
};

export type SubmitBuildQuestItemRequest = {
  userId: string;
  questionId: string;
  answer: string;
};

export type SubmitBuildQuestItemResponse = GetBuildQuestStatusResponse;
